import React, { useState } from "react";
import { Button } from "./ui/button";
import { Bot, Upload, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Shared logo upload widget — preview + upload + replace + remove.
 *
 * Reuses the single backend endpoint `POST /api/branding/logo` (and DELETE
 * counterpart), which updates BOTH `companies.logo_url` AND `chatbots.logo_url`
 * server-side, so any caller (Business Profile, Branding, etc.) stays in sync.
 *
 * Props:
 *   value           — current logo_url (relative path like `/api/branding/files/...`).
 *   onChange(url)   — called with the new logo_url after upload (or `""` after remove).
 *   testIdPrefix    — optional prefix to disambiguate selectors when used twice on one page.
 *                     Defaults to `"branding"` (matches existing Branding page test IDs).
 */
const ALLOWED_MIMES = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/tiff"];
const MAX_BYTES = 2 * 1024 * 1024;

export default function LogoUploader({ value, onChange, testIdPrefix = "branding" }) {
  const [uploading, setUploading] = useState(false);
  const logoSrc = value ? `${process.env.REACT_APP_BACKEND_URL}${value}` : "";

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED_MIMES.includes(f.type)) {
      toast.error("Logo must be a PNG, JPG, WEBP, or TIFF image.");
      e.target.value = ""; return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("Logo too large (max 2 MB).");
      e.target.value = ""; return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/branding/logo", fd);
      onChange?.(data.logo_url);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onRemove = async () => {
    try {
      await api.delete("/branding/logo");
      onChange?.("");
      toast.success("Logo removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove logo");
    }
  };

  return (
    <div className="flex items-center gap-4" data-testid={`${testIdPrefix}-logo-uploader`}>
      <div
        className="w-16 h-16 rounded-lg bg-muted grid place-items-center overflow-hidden border"
        data-testid={`${testIdPrefix}-logo-preview`}
      >
        {logoSrc ? (
          <img src={logoSrc} alt="logo" className="max-w-full max-h-full object-contain" />
        ) : (
          <Bot className="w-8 h-8 text-muted-foreground" />
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/tiff"
            onChange={onUpload}
            disabled={uploading}
            className="hidden"
            data-testid={`${testIdPrefix}-logo-input`}
          />
          <Button
            asChild
            size="sm"
            variant="outline"
            disabled={uploading}
            data-testid={`${testIdPrefix}-logo-upload`}
          >
            <span>
              {uploading ? (
                <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Uploading…</>
              ) : (
                <><Upload className="w-3 h-3 mr-1.5" />{logoSrc ? "Replace logo" : "Upload logo"}</>
              )}
            </span>
          </Button>
        </label>
        {logoSrc && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onRemove}
            data-testid={`${testIdPrefix}-logo-remove`}
            className="text-muted-foreground"
          >
            <Trash2 className="w-3 h-3 mr-1.5" />Remove
          </Button>
        )}
        <div className="text-xs text-muted-foreground">PNG, JPG, WEBP, TIFF. Max 2 MB.</div>
      </div>
    </div>
  );
}
